import React from "react";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "wouter";
import { CallbackScheduler } from "@/components/CallbackScheduler";
import { ContactGoogleReviews } from "@/components/ContactGoogleReviews";
import { StructuredData } from "@/components/StructuredData";
import { companyAbout, teamMembers } from "@/data/contact";
import { assets, siteConfig, siteIdentityLinks } from "@/data/site";

export default function Contact() {
  const pageUrl = `${siteConfig.url}/contact-partition-stalls`;
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
          { "@type": "ListItem", position: 2, name: "Schedule a Callback", item: pageUrl },
        ],
      },
      {
        "@type": ["ContactPage", "AboutPage"],
        name: "Schedule a Partition Stalls Callback",
        url: pageUrl,
        mainEntity: {
          "@type": ["Organization", "LocalBusiness"],
          "@id": `${siteConfig.url}/#business`,
          name: siteConfig.name,
          legalName: siteConfig.legalName,
          url: siteConfig.url,
          logo: `${siteConfig.url}${assets.logo}`,
          email: siteConfig.email,
          telephone: siteConfig.phoneHref,
          address: {
            "@type": "PostalAddress",
            streetAddress: siteConfig.address.streetAddress,
            addressLocality: siteConfig.address.addressLocality,
            addressRegion: siteConfig.address.addressRegion,
            postalCode: siteConfig.address.postalCode,
            addressCountry: siteConfig.address.addressCountry,
          },
          areaServed: siteConfig.serviceArea,
          sameAs: siteIdentityLinks,
          hasMap: siteConfig.address.googleBusinessProfile,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "sales",
            telephone: siteConfig.phoneHref,
            email: siteConfig.email,
            areaServed: "US",
          },
          employee: teamMembers.map((member) => ({
            "@type": "Person",
            name: member.name,
            jobTitle: member.title,
            image: `${siteConfig.url}${member.image}`,
          })),
        },
      },
    ],
  };

  return (
    <>
      <StructuredData data={structuredData} />
      <header className="contact-booking-hero section-line">
        <div className="contact-booking-hero__intro">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span>Contact</p>
          <span className="kicker">Fastest way to reach our team</span>
          <h1 id="contact-callback-heading">Choose a time.<br />We’ll call you.</h1>
          <p>Enter your name and number, then reserve an open 30-minute window today or on an upcoming business day. No hold time and no long contact form.</p>
          <ul className="contact-booking-hero__benefits" aria-label="Callback scheduling details">
            <li><span>01</span><strong>Name, phone, and time only</strong></li>
            <li><span>02</span><strong>30-minute reserved callback</strong></li>
            <li><span>03</span><strong>Today plus the next 10 business days</strong></li>
            <li><span>04</span><strong>Weekdays, 10:00 AM–5:00 PM Eastern</strong></li>
          </ul>
          <p className="contact-booking-hero__coverage"><MapPin aria-hidden="true" /> Full-service support in NY, NJ, PA, CT, FL, TX, and CA. Materials ship nationwide.</p>
        </div>
        <div className="contact-booking-hero__form">
          <div className="contact-booking-hero__form-label"><span>Upcoming availability</span><strong>Reserve your callback</strong></div>
          <CallbackScheduler sourcePath="/contact-partition-stalls" headingId="contact-callback-heading" showHeading={false} />
          <p className="contact-booking-hero__quote-shortcut">Need pricing or a plan review? <Link href="/request-a-quote">Start a simple quote request</Link> with your contact details and project location.</p>
          <ContactGoogleReviews />
        </div>
      </header>
      <section className="contact-alternatives section-line" aria-labelledby="contact-alternatives-heading">
        <div className="contact-alternatives__heading">
          <span className="kicker">Other ways to connect</span>
          <h2 id="contact-alternatives-heading">Prefer to call, email, or send project details?</h2>
          <p>The scheduled callback is the quickest guided option. These direct paths remain available when they fit your project better.</p>
        </div>
        <div className="contact-alternatives__grid" aria-label="Other ways to contact Partition Stalls">
          <a href={`tel:${siteConfig.phoneHref}`} className="contact-action">
            <span className="contact-action__top"><Phone aria-hidden="true" /> Call our team</span>
            <strong>{siteConfig.phoneDisplay}</strong>
            <span className="contact-action__link">Call now <ArrowUpRight aria-hidden="true" /></span>
          </a>
          <a href={`mailto:${siteConfig.email}`} className="contact-action">
            <span className="contact-action__top"><Mail aria-hidden="true" /> Email sales support</span>
            <strong>{siteConfig.email}</strong>
            <span className="contact-action__link">Start an email <ArrowUpRight aria-hidden="true" /></span>
          </a>
          <address className="contact-action contact-action--address">
            <span className="contact-action__top"><MapPin aria-hidden="true" /> Business address</span>
            <strong>{siteConfig.address.display}</strong>
            <a href={siteConfig.address.googleBusinessProfile} target="_blank" rel="noopener noreferrer" className="contact-action__link" aria-label={`Open Partition Stalls at ${siteConfig.address.display} in Google Maps`}>Open in Google Maps <ArrowUpRight aria-hidden="true" /></a>
          </address>
          <Link href="/request-a-quote" className="contact-action contact-action--quote">
            <span className="contact-action__top"><ArrowUpRight aria-hidden="true" /> Plans, pricing, or quantities</span>
            <strong>Request a free quote</strong>
            <span className="contact-action__link">Open quote form <ArrowUpRight aria-hidden="true" /></span>
          </Link>
        </div>
      </section>
      <section className="contact-company section-line" aria-labelledby="contact-company-heading">
        <div>
          <span className="kicker">Who we are</span>
          <h2 id="contact-company-heading">{companyAbout.heading}</h2>
        </div>
        <div className="contact-company__copy">
          {companyAbout.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          <Link href="/service-areas">Review service coverage <ArrowUpRight aria-hidden="true" /></Link>
        </div>
      </section>
      <section className="contact-team section-line" aria-labelledby="contact-team-heading">
        <header className="contact-team__header">
          <div>
            <span className="kicker">The people behind the projects</span>
            <h2 id="contact-team-heading">Meet the Partition Stalls team.</h2>
          </div>
          <p>Sales, project development, and project-management support work together in one place. Call or email us and we’ll route your question to the right person.</p>
        </header>
        <div className="contact-team__grid">
          {teamMembers.map((member, index) => (
            <article key={member.id} data-team-member={member.id}>
              <figure><img src={member.image} alt={member.imageAlt} width="720" height="900" loading="lazy" decoding="async" /></figure>
              <div><span>0{index + 1}</span><h3>{member.name}</h3><p>{member.title}</p></div>
            </article>
          ))}
        </div>
      </section>
      <section className="contact-final section-line" aria-labelledby="contact-final-heading">
        <div><span className="kicker">Ready when you are</span><h2 id="contact-final-heading">Tell us what the project needs.</h2></div>
        <div>
          <p>Use the quote form for pricing, plans, schedules, and quantities. For a quick question, call or email the team directly.</p>
          <div className="button-row">
            <Link href="/request-a-quote" className="button button--primary">Request a Quote <ArrowUpRight aria-hidden="true" /></Link>
            <a href={`tel:${siteConfig.phoneHref}`} className="button button--outline">Call {siteConfig.phoneDisplay}</a>
          </div>
        </div>
      </section>
    </>
  );
}
